/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/**
 * Pure helpers for the mobile session handoff flow. These are intentionally
 * side-effect free (beyond DOM parsing) so they can be unit tested in isolation
 * from the component's rendering and navigation side effects.
 */

/** The account portion of an injected guest-auth payload. */
export interface GuestAuthAccount {
    account_id: string;
}

/**
 * The base64-encoded JSON structure a native app injects via the
 * `X-Mobile-Guest-Auth` cookie for an anonymous (guest) checkout session.
 */
export interface GuestAuth {
    is_guest: boolean;
    access_token: string;
    account: GuestAuthAccount;
    expiration: string;
}

/**
 * Returns whether `uri` targets the same host as the current page.
 *
 * This is the open-redirect guard for the `next` parameter: relative paths and
 * same-origin absolute URLs are allowed, while cross-origin, protocol-relative
 * (`//evil.example.com`), and non-navigational (`javascript:`) URIs are rejected.
 * Resolution is delegated to the WHATWG URL parser (relative to the current
 * location) so the outcome is deterministic and matches how a real navigation
 * would resolve the value. A URI that can not be parsed is treated as unsafe.
 *
 * @param uri - The candidate target URI (may be null when the param is absent).
 * @returns `true` only when the resolved hostname matches the current hostname.
 */
export function isSameOriginUri(uri: string | null): boolean {
    if (!uri) {
        return false;
    }

    try {
        const resolved = new URL(uri, window.location.href);
        return resolved.hostname === window.location.hostname;
    } catch {
        return false;
    }
}

/**
 * Type guard validating the decoded guest-auth payload before it is trusted.
 *
 * Mirrors the structural checks the handoff relies on: the payload must be an
 * object carrying a truthy `is_guest` flag, an `access_token`, an `account` with
 * an `account_id`, and an `expiration`. Anything else is treated as malformed.
 *
 * @param value - The result of `JSON.parse(atob(cookie))`, of unknown shape.
 * @returns `true` (narrowing to `GuestAuth`) when every required field is present.
 */
export function isValidGuestAuth(value: unknown): value is GuestAuth {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const auth = value as Record<string, unknown>;
    const account = auth.account;

    const hasValidAccount =
        typeof account === 'object' &&
        account !== null &&
        Boolean((account as Record<string, unknown>).account_id);

    return (
        Boolean(auth.is_guest) &&
        Boolean(auth.access_token) &&
        hasValidAccount &&
        Boolean(auth.expiration)
    );
}
